import {
    getObjects,
    createObjects,
    updateObjects,
    deleteObjects,
} from './../api/webApplicationApi';
import { v4 as uuidv4 } from 'uuid';
import { Role } from './role';
import { AirCurtain } from './airCurtain';
import { UserGroup } from './userGroup';

const objectType = 'user';

export class User {
    private _saveMethod = createObjects;
    private _object: omUser;
    private _lastError: Error;

    public constructor(userID: string) {
        this._object = {
            userID: userID,
            userGroups: [],
            accountID: undefined,
            identityID: undefined,
            headObjectID: undefined,
            headObjectType: undefined,
            name: undefined,
            firstName: undefined,
            lastName: undefined,
            airCurtains: [],
            roles: [],
            invitationID: undefined,
            acceptedInvitationDate: undefined,
        };
    }

    public static createNew(): User {
        return new User(uuidv4());
    }

    public static createFromObject(user: omUser): User {
        const obj = new User(user.userID);
        obj._object = user;
        obj._saveMethod = updateObjects;
        return obj;
    }

    public async load(): Promise<boolean> {
        const getObject: GetRequest = {
            objectType: objectType,
            keys: [this._object.userID],
        };
        this._lastError = undefined;
        try {
            const response = await getObjects([getObject]);
            if (
                response &&
                response.length > 0 &&
                response[0].objects &&
                response[0].objects.length == 1
            ) {
                this._saveMethod = updateObjects;
                this._object = response[0].objects[0];
                return true;
            } else {
                this._lastError = new Error('user not found');
                return false;
            }
        } catch (error: any) {
            this._lastError = error;
            return false;
        }
    }

    public async save(): Promise<boolean> {
        const saveObject: CreateUpdateDeleteRequest = {
            objectType: objectType,
            object: this._object,
        };
        this._lastError = undefined;

        // if (this._object.name == undefined) {
        //     this._lastError = new Error('Name is undefined');
        //     return false;
        // }
        if (this._object.accountID == undefined) {
            this._lastError = new Error('Account ID is undefined');
            return false;
        }
        try {
            const response = await this._saveMethod([saveObject]);
            if (response) {
                this._saveMethod = updateObjects;
            } else {
                this._lastError = new Error('unknow server error');
            }
            return response;
        } catch (error: any) {
            this._lastError = error;
            return false;
        }
    }

    public async delete(): Promise<boolean> {
        const deleteObject: CreateUpdateDeleteRequest = {
            objectType: objectType,
            object: this._object,
        };
        this._lastError = undefined;
        try {
            const response = await deleteObjects([deleteObject]);
            return response;
        } catch (error: any) {
            this._lastError = error;
            return false;
        }
    }

    public get lastError(): Error {
        return this._lastError;
    }

    public get userID(): string {
        return this._object.userID;
    }

    public get accountID(): string {
        return this._object.accountID;
    }

    public set accountID(accountID: string) {
        this._object.accountID = accountID;
    }

    public get identityID(): string {
        return this._object.identityID;
    }

    public set identityID(identityID: string) {
        this._object.identityID = identityID;
    }

    public get name(): string {
        return this._object.name;
    }

    public set name(name: string) {
        this._object.name = name;
    }

    public get firstName(): string {
        return this._object.firstName;
    }

    public set firstName(firstName: string) {
        this._object.firstName = firstName;
    }

    public get lastName(): string {
        return this._object.lastName;
    }

    public set lastName(lastName: string) {
        this._object.lastName = lastName;
    }

    public get headObjectType(): string {
        return this._object.headObjectType;
    }

    public set headObjectType(headObjectType: string) {
        this._object.headObjectType = headObjectType;
    }

    public get headObjectID(): string {
        return this._object.headObjectID;
    }

    public set headObjectID(headObjectID: string) {
        this._object.headObjectID = headObjectID;
    }

    public get userGroups(): Array<string> {
        return this._object.userGroups;
    }

    public get invitationID(): string {
        return this._object.invitationID;
    }

    public set invitationID(invitationID: string) {
        this._object.invitationID = invitationID;
    }

    public get acceptedInvitationDate(): Date {
        return this._object.acceptedInvitationDate;
    }

    public set acceptedInvitationDate(acceptedInvitationDate: Date) {
        this._object.acceptedInvitationDate = acceptedInvitationDate;
    }

    public addUserGroup(userGroup: UserGroup): void {
        if (this._object.userGroups.includes(userGroup.userGroupID)) return;
        this._object.userGroups.push(userGroup.userGroupID);
    }

    public deleteUserGroup(userGroup: UserGroup): void {
        const index = this._object.userGroups.indexOf(userGroup.userGroupID);
        if (index > -1) {
            this._object.userGroups.splice(index, 1);
        }
    }

    public get roles(): string[] {
        return this._object.roles;
    }

    public addRole(role: Role): void {
        if (this._object.roles.includes(role.roleID)) return;
        this._object.roles.push(role.roleID);
    }

    public deleteRole(role: Role): void {
        const index = this._object.roles.indexOf(role.roleID);
        if (index > -1) {
            this._object.roles.splice(index, 1);
        }
    }

    public get airCurtains(): string[] {
        return this._object.airCurtains;
    }

    public addAirCurtain(airCurtain: AirCurtain): void {
        if (this._object.airCurtains.includes(airCurtain.airCurtainID)) return;
        this._object.airCurtains.push(airCurtain.airCurtainID);
    }

    public deleteAirCurtain(airCurtain: AirCurtain): void {
        const index = this._object.airCurtains.indexOf(airCurtain.airCurtainID);
        if (index > -1) {
            this._object.airCurtains.splice(index, 1);
        }
    }
}
